import { useEffect, useState } from "react";
import { useOffersEnabled } from "../../../api/textFeatures";
import { toast } from "sonner";
import { shiftApi } from "../../../api/resources";
import { errorMessage } from "../../../api/client";
import type { CrewResponse, ShiftAdmin } from "../../../api/types";
import { cn } from "../../../components/ui";

const OPTIONS: [CrewResponse, string][] = [["PENDING", "Awaiting reply"], ["CONFIRMED", "Confirmed"], ["DECLINED", "Declined"]];

export function ResponsePanel({ s, onChanged }: { s: ShiftAdmin; onChanged: (s: ShiftAdmin) => void }) {
  const [busy, setBusy] = useState(false);
  const pick = async (r: CrewResponse) => {
    setBusy(true);
    try { onChanged(await shiftApi.setResponse(s.id, r)); toast.success("Crew response updated"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const when = s.employee_response_at ? new Date(s.employee_response_at).toLocaleString() : null;
  return (
    <section data-testid="drawer-crew-response">
      <p className="label-caps mb-2 text-zinc-400">Crew response</p>
      <div className="inline-flex rounded-full border border-white/15 p-1" role="radiogroup" aria-label="Crew response">
        {OPTIONS.map(([v, label]) => (
          <button key={v} type="button" role="radio" aria-checked={s.employee_response === v} disabled={busy} onClick={() => pick(v)}
            data-testid={`drawer-response-${v.toLowerCase()}`}
            className={cn("rounded-full px-3 py-1 text-[11px] uppercase tracking-wider transition-colors disabled:opacity-50",
              s.employee_response === v ? (v === "DECLINED" ? "bg-signal text-white" : "bg-white/15 text-white") : "text-zinc-400 hover:text-white")}>{label}</button>
        ))}
      </div>
      <p className="mt-2 text-xs text-zinc-500" data-testid="drawer-response-meta">
        {s.employee_response ? `${when ? `Set ${when}` : "Set"}${s.employee_response_source ? ` · by ${s.employee_response_source === "SMS" ? "text reply" : "admin"}` : ""}` : "No crew response requested."}
        {s.employee_response === "DECLINED" && " Assign someone else, then cancel this shift."}
      </p>
      {!!s.late_notices?.length && (
        <ul className="mt-3 space-y-1 text-xs text-warn" data-testid="drawer-late-notices">
          {s.late_notices.map((n, i) => <li key={i}>Texted LATE{n.minutes ? ` ${n.minutes} min` : ""} · {new Date(n.received_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</li>)}
        </ul>
      )}
    </section>
  );
}

export function NotifyCrewToggle({ checked, onChange, hint, testId }: { checked: boolean; onChange: (v: boolean) => void; hint: string; testId: string }) {
  const on = useOffersEnabled();
  useEffect(() => { if (on === false && checked) onChange(false); }, [on, checked, onChange]);
  return (
    <label className={cn("flex items-start gap-3 text-sm text-zinc-300", !on && "opacity-60")}>
      <input type="checkbox" checked={checked && !!on} disabled={!on} onChange={(e) => onChange(e.target.checked)} className="mt-1 accent-signal" data-testid={testId} />
      <span><span className="font-medium text-white">Text crew to confirm</span>
        <span className="block text-xs text-zinc-500" data-testid={`${testId}-hint`}>{on === false ? "Shift offer texts are off" : hint}</span></span>
    </label>
  );
}
